import React, { useEffect, useState } from "react";
import { Location as TQSLocation } from "../../models/location";
import CopyButton from "../ui/CopyButton";
import OpsManagers from "../ui/OpsManagers";

export default function Dropdown({
  location,
  open,
}: {
  location: TQSLocation | null | undefined;
  open: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location) {
      setIsLoading(false);
    }
  }, [location]);

  useEffect(() => {
    setIsOpen(open); // Update the open state based on the prop
  }, [open]);

  const rotateChev = () => setIsOpen(!isOpen);

  function formatNumber(number: string): string {
    if (number === undefined || number === null) return "N/A";

    const cleaned = number.replace(/\D/g, "");
    const formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    return formatted;
  }

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="skeleton h-4 w-28"></div>
        <div className="skeleton h-4 w-full"></div>
        <div className="skeleton h-4 w-full"></div>
      </div>
    );
  }

  return (
    <div className="collapse bg-white">
      <input type="checkbox" checked={isOpen} onClick={rotateChev} />
      <div className="collapse-title flex flex-row items-center text-xl font-medium text-tqsBlack">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="#212322"
          className={`mr-4 size-6 transition-transform duration-200 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
        <img
          className="mr-3 h-8 w-8"
          src={
            (location?.oemPicture as any)?.s3Code
              ? (location?.oemPicture as any)?.s3Code
              : "/dashboard/avatar.png"
          }
          alt=""
        />
        <div>
          <p className="text-tqsBlack">{location?.name}</p>
          <p className="text-sm text-gray-500">
            {(location?.oem as any)?.name ?? "Other"}
          </p>
        </div>
        <OpsManagers location={location} />
      </div>

      <div className="collapse-content no-animation">
        <div className="overflow-hidden rounded-lg bg-white">
          {/* Header row */}
          <div className="flex border-b-2 text-sm font-medium text-gray-500">
            <div className="flex-1 px-4 py-2">Liaison</div>
            <div className="flex-1 px-4 py-2">Primary Location</div>
            <div className="flex-1 px-4 py-2">Email</div>
            <div className="flex-1 px-4 py-2">Phone</div>
          </div>

          {location?.agreement?.assignedReps
            .filter(rep => (!rep.endTime || new Date(rep.endTime) >= new Date()))
            .filter((rep, index, array) => {
              return (
                array.findIndex(
                  (r) => r.representative.email === rep.representative.email,
                ) === index
              );
            })
            .map((rep, index) => (
              <div className="mt-2 flex items-center p-4" key={index}>
                <div className="flex min-w-0 flex-1 items-center">
                  <img
                    className="mr-3 h-8 w-8 rounded-full"
                    src={
                      rep.representative.photoAttachment?.downloadURL
                        ? rep.representative.photoAttachment?.downloadURL
                        : "/dashboard/avatar.png"
                    }
                    alt={`${rep.representative.firstName} ${rep.representative.lastName}`}
                  />
                  <div>
                    <p className="truncate text-sm font-medium text-tqsBlack">
                      {rep?.representative.firstName}{" "}
                      {rep?.representative.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {rep?.representative.primaryContact
                        ? "primary contact"
                        : "cross-trained"}
                    </p>
                  </div>
                </div>
                <div className="ml-2 flex-1">
                  <span className="rounded-md bg-[#edeeee] px-3.5 py-1.5 text-xs font-medium text-tqsBlack">
                    {location?.name}
                  </span>
                </div>
                <div className="ml-6 flex flex-1 items-center">
                  <a
                    href={`mailto:${rep?.representative.email}`}
                    className="mr-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#73cffd] text-white"
                    title="Mail"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-[14px] w-[14px]"
                    >
                      <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                      <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                    </svg>
                  </a>
                  <CopyButton text={rep?.representative?.email}>
                    <span className="whitespace-nowrap py-1.5 text-xs font-medium text-gray-400">
                      {rep?.representative.email}
                    </span>
                  </CopyButton>
                </div>
                <div className="flex flex-1 items-center">
                  <button
                    className="ml-4 mr-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#3a7bfc] text-white"
                    title="Phone"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-[14px] w-[14px]"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97ZM6.75 8.25a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H7.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <CopyButton text={rep?.representative?.phone ?? "N/A"}>
                    <span className="whitespace-nowrap py-1.5 text-xs font-medium text-gray-400">
                      {formatNumber(rep?.representative?.phone) ?? "N/A"}
                    </span>
                  </CopyButton>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
